import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark } from "@fortawesome/pro-regular-svg-icons";

import Input from "components/form/input";

interface IStatisticsBarExpandableSearchProps {
  placeholder: string;
  setDebouncedTeeTimeSearch: (teeTimeSearch: string) => void;
  mobileOverlay?: boolean;
}

export default function StatisticsBarExpandableSearch(props: IStatisticsBarExpandableSearchProps) {
  const { placeholder, setDebouncedTeeTimeSearch } = props;
  const [teeTimeSearch, setTeeTimeSearch] = useState("");
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastDispatchedRef = useRef<string | null>(null);
  const setDebouncedRef = useRef(setDebouncedTeeTimeSearch);
  setDebouncedRef.current = setDebouncedTeeTimeSearch;

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (lastDispatchedRef.current !== teeTimeSearch) {
        lastDispatchedRef.current = teeTimeSearch;
        setDebouncedRef.current(teeTimeSearch);
      }
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, [teeTimeSearch]);

  useEffect(() => {
    if (!expanded) {
      return;
    }

    const timeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 180);

    return () => {
      clearTimeout(timeout);
    };
  }, [expanded]);

  useEffect(() => {
    if (!expanded) {
      return;
    }

    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        if (!teeTimeSearch) {
          setExpanded(false);
        }
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [expanded, teeTimeSearch]);

  function openSearch() {
    setExpanded(true);
  }

  function closeSearch() {
    setExpanded(false);
    if (teeTimeSearch) {
      setTeeTimeSearch("");
      lastDispatchedRef.current = null;
      setDebouncedRef.current("");
    }
  }

  const isExpanded = props.mobileOverlay ? expanded : expanded || teeTimeSearch.length > 0;

  if (props.mobileOverlay) {
    return (
      <div
        ref={containerRef}
        className={classNames("statistics-bar-filter-search", "statistics-bar-filter-search--mobile-overlay", {
          "statistics-bar-filter-search--expanded": isExpanded,
        })}
      >
        {!isExpanded ? (
          <button
            type="button"
            className="statistics-bar-filter-search-prefix"
            aria-label={placeholder}
            aria-expanded={expanded}
            onClick={openSearch}
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        ) : (
          <div className="statistics-bar-filter-search-input">
            <Input
              labelHidden
              placeholder={placeholder}
              value={teeTimeSearch}
              onChange={e => setTeeTimeSearch(e.target.value)}
              inputRef={inputRef}
              prefix={
                <button
                  type="button"
                  className="statistics-bar-filter-search-prefix-btn"
                  aria-label="Close search"
                  onClick={closeSearch}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              }
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={classNames("statistics-bar-filter-search", {
        "statistics-bar-filter-search--expanded": isExpanded,
      })}
    >
      {!isExpanded && (
        <button
          type="button"
          className="statistics-bar-filter-search-toggle"
          aria-label={placeholder}
          aria-expanded={expanded}
          onClick={openSearch}
        >
          <FontAwesomeIcon icon={faSearch} />
        </button>
      )}
      <div className="statistics-bar-filter-search-input">
        <Input
          labelHidden
          placeholder={placeholder}
          value={teeTimeSearch}
          onChange={e => setTeeTimeSearch(e.target.value)}
          prefix={<FontAwesomeIcon icon={faSearch} />}
          inputRef={inputRef}
        />
      </div>
    </div>
  );
}
